import React, { useEffect, useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import style from "./ActorManagement.module.css";
import CastCrewMemberModal from "../../component/CastCrewMemberModal/CastCrewMemberModal";
import { castCrewMemberDelete, castCrewMemberList } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import { useRbac } from "../../rbac/RbacContext";

const ActorManagement = () => {
  const { can } = useRbac();
  // Cast/crew management sits under the "users" module.
  const canCreateActor = can("users", "create");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchActors = async () => {
    try {
      setLoading(true);
      const response = await castCrewMemberList(50, 1);
      const list =
        response?.data?.data?.items ||
        response?.data?.data?.data ||
        response?.data?.data ||
        [];
      setActors(Array.isArray(list) ? list : []);
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to load actors",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActors();
  }, []);

  const openAddModal = () => {
    setEditData(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  const handleEdit = (actor) => {
    setEditData(actor);
    setIsModalOpen(true);
  };

  const handleDelete = async (actor) => {
    const confirmed = window.confirm(
      `Delete "${actor.name}"? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      setDeletingId(actor.id);
      const response = await castCrewMemberDelete(actor.id);
      showToast(
        response?.data?.message || "Deleted successfully",
        "success"
      );
      setActors((prev) => prev.filter((a) => a.id !== actor.id));
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Failed to delete",
        "error"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleSuccess = () => {
    closeModal();
    fetchActors();
  };

  const formatDob = (dob) => {
    if (!dob) return "";
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return dob;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  return (
    <div className={style.container}>
      <div className={style.headerRow}>
        <div className={style.titleBox}>
          <div className={style.title}>Actor Management</div>
          <div className={style.subtitle}>
            Manage all content classification categories used across the platform
          </div>
        </div>
        {canCreateActor && (
          <button type="button" className={style.addBtn} onClick={openAddModal}>
            Add New
          </button>
        )}
      </div>

      {loading ? (
        <div className={style.emptyState}>Loading…</div>
      ) : actors.length === 0 ? (
        <div className={style.emptyState}>
          No actors added yet. Click "Add New" to create one.
        </div>
      ) : (
        <div className={style.grid}>
          {actors.map((actor) => (
            <div key={actor.id} className={style.card}>
              <div className={style.avatarWrap}>
                {actor.image_url ? (
                  <img
                    src={actor.image_url}
                    alt={actor.name}
                    className={style.avatar}
                  />
                ) : (
                  <div className={style.placeholder} />
                )}
                <div className={style.hoverOverlay}>
                  <button
                    type="button"
                    className={style.iconBtn}
                    onClick={() => handleEdit(actor)}
                    aria-label={`Edit ${actor.name}`}
                    title="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    type="button"
                    className={`${style.iconBtn} ${style.danger}`}
                    onClick={() => handleDelete(actor)}
                    disabled={deletingId === actor.id}
                    aria-label={`Delete ${actor.name}`}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              <div className={style.cardName}>{actor.name}</div>
              {actor.dob && (
                <div className={style.cardDate}>{formatDob(actor.dob)}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <CastCrewMemberModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSuccess={handleSuccess}
        editData={editData}
      />
    </div>
  );
};

export default ActorManagement;
